import React from "react"
import type {TypographyProps} from '@mui/material/Typography';
import Typography from "@mui/material/Typography";
import {v4 as uuidv4} from 'uuid';
import {RichTextElement} from "./CCORichText/CCORichTextHelper";
import {metaDataProperty} from "../preview/previewMetaData";
import {usePreviewContext} from "../../context/PreviewContext";

type Variant = TypographyProps['variant'];

export interface Props {
    textAsTree: RichTextElement,
    propertyName?: string,
    wordsLimit?: number;
    charLimit?: number;
    variant?: Variant;
    metadataId?: string;
}

function CCOPlainText(props: Props) {
    const isPreview = usePreviewContext().isPreview;
    if (!props.textAsTree) {
        return null
    }
    return <Typography variant={props.variant ?? 'body1'}
                       {...metaDataProperty(isPreview, props.propertyName, props.metadataId)}
                       sx={{
                           whiteSpace: 'pre-wrap',
                           overflowWrap: 'break-word', // Allow words to break with a dash
                           wordWrap: 'break-word', // Fallback for older browser support
                           hyphens: 'auto!important', // Enable automatic hyphenation
                           maxWidth: '100%',
                       }}>{
        renderElement(props.textAsTree, undefined, props.wordsLimit, props.charLimit, props.variant)
    }</Typography>
}

function renderElement(element: RichTextElement, key?: string, wordsLimit?: number,
                       charLimit?: number,
                       variant?: Variant) {
    const mapChildren = (e: RichTextElement, index: number) => renderElement(e, index.toString(),
                                                                             wordsLimit, charLimit,
                                                                             variant)

    switch (element._type) {
        case "Element": {
            // elements creating a <p>-tag
            switch (element.name) {
                case "p":
                case "h1":
                case "h2":
                case "h3":
                case "h4":
                case "h5":
                case "h6":
                case "li": {
                    return <span key={key}>
                        {element.children?.map(mapChildren)}
                    </span>
                }
                case "strong": {
                    return (
                        <Typography key={key} component="strong" variant={variant ?? 'body1'}
                                           sx={{
                                               fontWeight: 'bold'
                                           }}>
                            {element.children?.map(mapChildren)}
                        </Typography>
                    );
                }
                // all others
                default: {
                    return <span key={key}>{element.children?.map(mapChildren)} </span>
                }
            }
        }
        case "EmptyElement": {
            switch (element.name) {
                case "br": {
                    return <br key={key}/>
                }
                default: {
                    return <React.Fragment key={key}></React.Fragment>;
                }
            }
        }
        case "Characters": {
            return renderCharacters(element, charLimit, wordsLimit)
        }
        default: {
            throw Error(`Unsupported RichTextElement type '${element._type}'`)
        }
    }
}

export default CCOPlainText

export function renderCharacters(element: RichTextElement, charLimit: number | undefined,
                                 wordsLimit: number | undefined) {
    element.data?.replace('\uFEFF', ' ');
    return (
        <React.Fragment key={element.uuid ?? uuidv4()}>
            {charLimit ? element.data!.substring(0, charLimit) +
                         (element.data!.length > charLimit ? '...' : '') :
                wordsLimit && element.data!.split(/\s+/).length > wordsLimit ?
                    element.data!.split(/\s+/).slice(0, wordsLimit).join(' ') + '...'
                    : element.data}
        </React.Fragment>
    );
}